import {
  ANNOTATION_POPUP_BUTTONS_TO_KEEP,
  ANNOTATION_BUBBLE_RADIUS,
  ANNOTATION_BUBBLE_WIDTH,
  ANNOTATION_TOOLBAR_GROUP_BUTTON,
  TEXT_SELECT_TOOL,
  ANNOTATION_COMMENT_BUTTON,
  ANNOTATION_HEADER_RECTANGLE_TOOL,
  ANNOTATION_RECTANGLE_TOOL,
  ANNOTATION_RECTANGLE_FILL_COLOR,
  ANNOTATION_RECTANGLE_STROKE_COLOR,
  ANNOTATION_RECTANGLE_THICKNESS,
  ANNOTATION_HIGHLIGHT_TOOL,
  ANNOTATION_HIGHLIGHT_COLOR,
  ANNOTATION_COMMENT_CONTEXTUAL_BUTTON,
  ANNOTATION_SELECT_TOOL_BUTTON,
  ANNOTATION_RECTANGLE_TOOL_BUTTON,
  ANNOTATION_ORDINAL_BUBBLE_HEIGHT,
  ANNOTATION_ORDINAL_BUBBLE_WIDTH,
} from '../constants/pdf-annotations'

import { waitForElement } from '@app/utils/domHelpers'

import rectangleCommentIcon from '@app/shared/assets/icons/svg/rectangle_comment.svg?inline'
import communicationIcon from '@app/shared/assets/icons/svg/communication_icon.svg?inline'
import textHighlightCommentIcon from '@app/shared/assets/icons/svg/text_highlight_comment.svg?inline'
import commentBubbleIcon from '@app/shared/assets/icons/svg/comment_bubble.svg?inline'
import I18n from 'i18n-js'

import { useDrawer } from '@app/shared/pinia/drawer'
import { useBubbleStore } from '@app/application/communication/store/bubbles'
import { isUsingNewLayout } from '@app/adapters/silverfin'

import getPiniaInstance from '@app/shared/pinia/instance'
import { storeToRefs } from 'pinia'

const drawerStore = useDrawer(getPiniaInstance())
const bubbleStore = useBubbleStore(getPiniaInstance())
const { ordinal } = storeToRefs(bubbleStore)

class PDFTronAnnotationsUI {
  constructor(manager) {
    this.manager = manager
    this.instance = manager.instance
    this.utils = manager.utils

    const { documentViewer } = this.instance.Core
    this.documentViewer = documentViewer
  }

  setup() {
    this._loadPrimaryColor()
    this._disableUIElements()
    this._disableHotkeys()
    this._setupCommentButton()
    this._moveRectangleTool()
    this._setupContextualHighlightTool()
    this._updateIcons()

    if (this.manager.showCommunicationToggle) {
      this._setToggleRightDrawerButton()
    }
  }

  // Not ideal, but we need this for stable system specs -
  // we need to know when annotations have been loaded before attempting to select one
  markIframeReady() {
    const webviewerIframe = document.querySelector('#webviewer iframe')
    webviewerIframe.classList.add('annotationsLoaded')
  }

  async updateAnnotationPopupStyles(button) {
    const iframeDoc = this.instance.UI.iframeWindow.document
    try {
      const commentBtn = await waitForElement(
        iframeDoc,
        `[data-element="${button}"]`
      )
      Object.assign(commentBtn.style, {
        background: this.primaryColorHex,
        height: '27px',
        width: '31px',
        borderRadius: '8px',
        margin: 0,
      })

      const iconElem = commentBtn.querySelector('.Icon')
      Object.assign(iconElem.style, {
        height: '11px',
        width: '12px',
      })

      const svgElem = iconElem.querySelector('svg')
      svgElem.style.display = 'block'

      const popupElem = commentBtn.closest('.Popup')
      Object.assign(popupElem.style, {
        background: this.primaryColorHex,
        opacity: 0.8,
        boxShadow: 'inset 0 -1px 0 0 rgba(0, 0, 0, 0.15)',
        border: `0.5px solid ${this.primaryDarkColorHex}`,
        borderRadius: '8px',
        marginTop: '-5px', // pull the popup up, closer to the trigger, to compensate for the reduced button size
      })
    } catch (e) {
      this.utils.handleError(e, {
        source: 'updateAnnotationPopupStyles',
        button,
      })
    }
  }

  enableHeader() {
    this.instance.UI.enableElements(['header'])
  }

  setupToolStyles() {
    this.documentViewer.getTool(ANNOTATION_HIGHLIGHT_TOOL).setStyles({
      StrokeColor: this.utils.colorFromRGB(ANNOTATION_HIGHLIGHT_COLOR),
    })

    this.documentViewer.getTool(ANNOTATION_RECTANGLE_TOOL).setStyles({
      FillColor: this.utils.colorFromRGB(ANNOTATION_RECTANGLE_FILL_COLOR),
      StrokeColor: this.utils.colorFromRGB(ANNOTATION_RECTANGLE_STROKE_COLOR),
      StrokeThickness: ANNOTATION_RECTANGLE_THICKNESS,
    })
  }

  redrawCurrentPage() {
    this.documentViewer.recalculateLayout([
      this.documentViewer.getCurrentPage(),
    ])
  }

  drawBubbles(canvas, annotations) {
    const { Annotations } = this.instance.Core
    const { Math } = this.instance.Core
    const ctx = new Annotations.CanvasHelper(canvas)

    annotations.forEach((annot) => {
      if (ordinal.value[annot.Id]) {
        const startingPointX = annot.X + annot.Width + 5
        const startingPointY = annot.Y - 5

        const rect = new Math.Rect(
          startingPointX,
          startingPointY - ANNOTATION_ORDINAL_BUBBLE_HEIGHT / 2,
          startingPointX + ANNOTATION_ORDINAL_BUBBLE_HEIGHT / 2,
          startingPointY
        )

        const rect2 = new Math.Rect(
          startingPointX,
          startingPointY - ANNOTATION_ORDINAL_BUBBLE_HEIGHT,
          startingPointX + ANNOTATION_ORDINAL_BUBBLE_WIDTH,
          startingPointY
        )

        ctx.drawRectangle(rect, {
          fillColor: this.manager.utils.colorFromRGB(this.primaryColorRGB),
        })

        ctx.drawCircle(rect2, {
          fillColor: this.manager.utils.colorFromRGB(this.primaryColorRGB),
        })

        ctx.drawText(
          ordinal.value[annot.Id],
          startingPointX + 4,
          startingPointY - 9,
          {
            fillColor: this.manager.utils.colorFromRGB(this.subtleColorRGB),
            fontSize: 5,
            fontWeight: 'bold',
            fontFamily: 'Lato, sans-serif',
          }
        )
      } else {
        const radius = ANNOTATION_BUBBLE_RADIUS
        // offset that the circle is not drawn behind the annotation
        ctx.drawCircleXY(
          annot.X + annot.Width + radius + 5,
          annot.Y - 5,
          radius,
          {
            borderColor: this.manager.utils.colorFromRGB(this.primaryColorRGB),
            borderWidth: ANNOTATION_BUBBLE_WIDTH,
          }
        )
      }
    })
  }

  _disableHotkeys() {
    // disable all hotkeys for tools, e.g. "T" for free-text.
    // We hide the buttons in the UI, but the hotkeys still works if not explicitly disabled!
    Object.keys(this.instance.UI.hotkeys.Keys).forEach((key) => {
      this.instance.UI.hotkeys.off(key)
    })

    // disable delete/backspace on annotations
    const { Tools } = this.instance.Core
    const DELETE_KEY = 46
    const BACKSPACE = 8

    const originalKeyDown = Tools.AnnotationSelectTool.prototype.keyDown

    Tools.AnnotationSelectTool.prototype.keyDown = (e) => {
      if (!e || e.which === DELETE_KEY || e.which === BACKSPACE) {
        return
      }

      originalKeyDown.apply(this, [e])
    }
  }

  _disableUIElements() {
    const allAnnotationPopupButtons = this.instance.UI.annotationPopup
      .getItems()
      .map((item) => item.dataElement)
    const elementsToDisable = allAnnotationPopupButtons.filter(
      (name) => ANNOTATION_POPUP_BUTTONS_TO_KEEP.indexOf(name) === -1
    )

    this.instance.UI.disableElements(elementsToDisable)
  }

  _moveRectangleTool() {
    this.instance.UI.setHeaderItems((header) => {
      const rectangleTool = header
        .getHeader(ANNOTATION_TOOLBAR_GROUP_BUTTON)
        .getItems()
        .find((item) => item.toolGroup === ANNOTATION_HEADER_RECTANGLE_TOOL)
      const defaultHeader = header.getHeader('default')

      const headerItems = defaultHeader.getItems()

      const firstDividerIndex = headerItems.findIndex(
        (item) => item.type === 'divider'
      )
      const textSelectToolIndex = headerItems.findIndex(
        (item) => item.toolName === TEXT_SELECT_TOOL
      )

      headerItems.splice(firstDividerIndex, 1) // remove first divider
      headerItems.splice(textSelectToolIndex + 1, 0, rectangleTool) // insert rectangle tool after text select tool
      defaultHeader.update(headerItems)
    })
  }

  _setupCommentButton() {
    this.instance.UI.updateElement(ANNOTATION_COMMENT_BUTTON, {
      img: commentBubbleIcon,
      onClick: this.manager.onCommentButtonClick,
      title: '',
    })
  }

  _setupContextualHighlightTool() {
    this.instance.UI.textPopup.update([
      {
        dataElement: ANNOTATION_COMMENT_CONTEXTUAL_BUTTON,
        type: 'actionButton',
        img: commentBubbleIcon,
        onClick: this.manager.onHighlightedTextCommentButtonClick,
      },
    ])
  }

  _updateIcons() {
    this.instance.UI.updateElement(ANNOTATION_SELECT_TOOL_BUTTON, {
      img: textHighlightCommentIcon,
      title: I18n.t(
        'document.pdf_annotations.text_highlight_annotation_tooltip'
      ),
    })

    this.instance.UI.updateElement(ANNOTATION_RECTANGLE_TOOL_BUTTON, {
      img: rectangleCommentIcon,
      title: I18n.t('document.pdf_annotations.rectangle_annotation_tooltip'),
    })
  }

  _loadPrimaryColor() {
    const parentDoc = this.instance.UI.iframeWindow.parent.document
    const style = getComputedStyle(parentDoc.documentElement)
    this.primaryColorHex = style.getPropertyValue('--sf-color-primary-default')
    this.subtleColorHex = style.getPropertyValue('--sf-color-subtle-default')
    this.primaryDarkColorHex = style.getPropertyValue('--sf-color-primary-dark')
    this.primaryColorRGB = this.primaryColorHex
      .match(/\w\w/g)
      .map((x) => parseInt(x, 16))
    this.subtleColorRGB = this.subtleColorHex
      .match(/\w\w/g)
      .map((x) => parseInt(x, 16))
  }

  _setToggleRightDrawerButton() {
    if (isUsingNewLayout()) return

    this.instance.UI.setHeaderItems((header) => {
      header.push({
        type: 'actionButton',
        img: communicationIcon,
        onClick: () => {
          if (!drawerStore.isRightDrawerOpen) {
            drawerStore.openRightDrawer('communication')
          } else {
            drawerStore.closeRightDrawer()
          }
        },
      })
    })
  }
}

export default PDFTronAnnotationsUI
